import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
  Button,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Box
} from "@mui/material";
import debounce from "lodash/debounce";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { useListListQuery } from "store/services/listService";
import { useAuth } from "hooks/useAuth";
import { ENTITY_LIST_CONSTANTS } from "data/static";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";
import { useGetDDLSystemUserListQuery } from "store/services/userApi";
import { useLazySearchShipmentWithBranchQuery } from "store/services/shipmentApi";
import { useLazySearchContractorWithVehicleQuery } from "store/services/contractorApi";
import { useLazyCheckTransportRulesQuery } from "store/services/LocalTransportRuleApi";

const INITIAL_STATE = {
  branch: null,
  toBranch: null,
  transportationMode: "",
  distanceKm: "",
  estimatedCost: "",
  shipmentId: [],
  contractorId: null,
  vehicleId: null,
  actualCost: "",
  remarks: "",
  approver: null,
  noOfSacks: 0,
};

const LocalTransportForm = ({
  isBusy = false,
  localTransportDetail = null,
  onSubmit = () => { },
}) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin } = useAuth();

  // Local states
  const [shipmentSearchInput, setShipmentSearchInput] = useState("");
  const [contractorSearchInput, setContractorSearchInput] = useState("");
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [isToBranchEnabled, setIsToBranchEnabled] = useState(false);
  const [ruleOverride, setRuleOverride] = useState(false);
  const [modeChangeInfo, setModeChangeInfo] = useState(null);
  const lastRuleParamsRef = useRef(null);

  // Existing queries
  const [fetchBranches, { data: branches, isLoading: loadingBranches }] =
    useLazyGetDDLBranchListQuery();
  const { data: transportModeList = [], isLoading: loadingModes } =
    useListListQuery(
      {
        type: ENTITY_LIST_CONSTANTS.TRANSPORT_MODE,
        status: "Active",
      },
      { refetchOnMountOrArgChange: true }
    );

  const { data: approvers = [], isLoading: loadingApprovers } =
    useGetDDLSystemUserListQuery(
      {
        active: "true",
        entity: viewEntity?.entity?._id,
        typeRefSource: "System",
      },
      { skip: !viewEntity?.entity?._id }
    );

  // Rule checking query
  const [triggerRuleCheck, {
    data: ruleCheckResult,
    isLoading: checkingRules,
    isFetching: fetchingRules
  }] = useLazyCheckTransportRulesQuery();

  // Shipment and contractor queries
  const [
    triggerShipmentSearch,
    { data: shipments = [], isFetching: loadingShipments },
  ] = useLazySearchShipmentWithBranchQuery();

  const [
    triggerContractorSearch,
    { data: contractors = [], isFetching: loadingContractors },
  ] = useLazySearchContractorWithVehicleQuery();

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity,
      branch: viewEntity?.branch,
    },
  });

  // Watch fields
  const _watchContractor = useWatch({ control, name: "contractorId" });
  const watchBranch = useWatch({ control, name: "branch" });
  const watchToBranch = useWatch({ control, name: "toBranch" });
  const watchTransportMode = useWatch({ control, name: "transportationMode" });
  const watchDistance = useWatch({ control, name: "distanceKm" });
  const watchNoOfSacks = useWatch({ control, name: "noOfSacks" });

  // Fetch branches
  useEffect(() => {
    if (viewEntity?.entity?._id && viewEntity?.entity?.account?._id) {
      fetchBranches({
        entity: viewEntity.entity._id,
        account: viewEntity.entity.account._id,
      });
    }
  }, [viewEntity?.entity?._id, viewEntity?.entity?.account?._id, fetchBranches]);

  // Check rules when relevant fields change
  useEffect(() => {
    const checkRules = debounce(() => {
      if (!watchBranch || !watchTransportMode) {
        setModeChangeInfo(null);
        return;
      }

      if (!isToBranchEnabled && !watchDistance) {
        setModeChangeInfo(null);
        return;
      }

      const branchId = watchBranch?._id;
      const toBranchId = watchToBranch?._id || null;
      const distanceValue = isToBranchEnabled ? 0 : watchDistance;
      const lastParams = lastRuleParamsRef.current;

      const isModeChangedOnly =
        lastParams &&
        lastParams.branchId === branchId &&
        lastParams.toBranchId === toBranchId &&
        (isToBranchEnabled ? true : lastParams.distanceKm === distanceValue) &&
        lastParams.transportationMode !== watchTransportMode;

      if (isModeChangedOnly) {
        const fromLabel = lastParams.transportationMode;
        const toLabel = watchTransportMode;

        setModeChangeInfo({ from: fromLabel, to: toLabel });
        // Don't update lastRuleParamsRef.current here so that subsequent mode changes 
        // compare against the original rule parameters (anchor).
        return;
      }

      setModeChangeInfo(null);

      if (isToBranchEnabled) {
        triggerRuleCheck({
          branch: watchBranch,
          toBranch: watchToBranch,
          transportationMode: watchTransportMode,
          distanceKm: 0,
        });
      } else if (watchDistance) {
        triggerRuleCheck({
          branch: watchBranch,
          toBranch: watchToBranch,
          transportationMode: watchTransportMode,
          distanceKm: watchDistance,
        });
      }

      lastRuleParamsRef.current = {
        branchId,
        toBranchId,
        distanceKm: distanceValue,
        transportationMode: watchTransportMode,
      };
    }, 500);

    checkRules();

    return () => checkRules.cancel();
  }, [watchBranch, watchToBranch, watchTransportMode, watchDistance, triggerRuleCheck, isToBranchEnabled]);

  // Calculate cost based on pricePerSack and noOfSacks OR use suggestedCost
  // useEffect(() => {
  //   // Skip auto-calculation during edit mode
  //   if (localTransportDetail) return;

  //   // Only auto-fill if not manually overridden
  //   if (ruleOverride) return;

  //   if (
  //     isToBranchEnabled &&
  //     ruleCheckResult?.hasRule &&
  //     ruleCheckResult?.pricePerSack &&
  //     watchNoOfSacks > 0
  //   ) {
  //     // Calculate: pricePerSack × noOfSacks (for branch-to-branch)
  //     const calculatedCost = ruleCheckResult.pricePerSack * watchNoOfSacks;
  //     setValue("estimatedCost", calculatedCost);
  //   } else if (
  //     ruleCheckResult?.hasRule &&
  //     ruleCheckResult?.suggestedCost &&
  //     !isToBranchEnabled
  //   ) {
  //     // Use suggestedCost (for non-branch-to-branch)
  //     setValue("estimatedCost", ruleCheckResult.suggestedCost);
  //   }
  // }, [ruleCheckResult, watchNoOfSacks, isToBranchEnabled, setValue, ruleOverride, localTransportDetail]);

  // Reset override when conditions change
  useEffect(() => {
    setRuleOverride(false);
  }, [watchBranch, watchToBranch, watchTransportMode, watchDistance, watchNoOfSacks]);

  // Debounced shipment search
  const debouncedShipmentSearch = useCallback(
    debounce((term) => {
      if (viewEntity?.entity?._id && viewEntity?.entity?.account?._id) {
        triggerShipmentSearch({
          account: viewEntity.entity.account._id,
          entity: viewEntity.entity._id,
          query: term || "",
        });
      }
    }, 400),
    [viewEntity?.entity?._id, viewEntity?.entity?.account?._id, triggerShipmentSearch]
  );

  // Debounced contractor search
  const debouncedContractorSearch = useCallback(
    debounce((term) => {
      if (viewEntity?.entity?._id && viewEntity?.entity?.account?._id) {
        triggerContractorSearch({
          account: viewEntity.entity.account._id,
          entity: viewEntity.entity._id,
          query: term || "",
        });
      }
    }, 400),
    [viewEntity?.entity?._id, viewEntity?.entity?.account?._id, triggerContractorSearch]
  );

  // Effect for shipment search
  useEffect(() => {
    debouncedShipmentSearch(shipmentSearchInput);
    return () => debouncedShipmentSearch.cancel();
  }, [shipmentSearchInput, debouncedShipmentSearch]);

  // Effect for contractor search
  useEffect(() => {
    debouncedContractorSearch(contractorSearchInput);
    return () => debouncedContractorSearch.cancel();
  }, [contractorSearchInput, debouncedContractorSearch]);

  // Initial searches on mount
  useEffect(() => {
    if (viewEntity?.entity?._id && viewEntity?.entity?.account?._id) {
      triggerShipmentSearch({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
        query: "",
      });

      triggerContractorSearch({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
        query: "",
      });
    }
  }, [
    viewEntity?.entity?._id,
    viewEntity?.entity?.account?._id,
    triggerShipmentSearch,
    triggerContractorSearch
  ]);

  // Update vehicle options when contractor changes
  useEffect(() => {
    if (_watchContractor && _watchContractor.vehicle_id) {
      setVehicleOptions(_watchContractor.vehicle_id || []);
      setValue("vehicleId", null);
    } else {
      setVehicleOptions([]);
      setValue("vehicleId", null);
    }
  }, [_watchContractor, setValue]);

  const _mappedTransportModes = useMemo(
    () =>
      Array.isArray(transportModeList)
        ? transportModeList.map((item) => ({
          label: item?.name || "",
          value: item?._id || "",
        }))
        : [],
    [transportModeList]
  );

  const getModeLabel = useCallback(
    (id) => {
      if (!id) return "";
      const found = _mappedTransportModes.find((m) => m.value === id);
      return found?.label || "";
    },
    [_mappedTransportModes]
  );

  // Set form values when editing
  useEffect(() => {
    if (localTransportDetail) {
      const detailToReset = {
        ...INITIAL_STATE,
        ...localTransportDetail
      };

      if (localTransportDetail.shipmentId) {
        detailToReset.shipmentId = Array.isArray(localTransportDetail.shipmentId)
          ? localTransportDetail.shipmentId
          : [localTransportDetail.shipmentId];
      }

      if (localTransportDetail.contractorId) {
        detailToReset.contractorId = localTransportDetail.contractorId;
      }

      if (localTransportDetail.vehicleId) {
        detailToReset.vehicleId = localTransportDetail.vehicleId;
      }

      if (localTransportDetail.transportationMode) {
        detailToReset.transportationMode = localTransportDetail.transportationMode._id ||
          localTransportDetail.transportationMode;
      }

      if (localTransportDetail.toBranch) {
        setIsToBranchEnabled(true);
        detailToReset.toBranch = localTransportDetail.toBranch;
      } else {
        setIsToBranchEnabled(false);
      }

      // Set rule override based on whether the cost matches rule calculation
      if (localTransportDetail.estimatedCost && ruleCheckResult?.hasRule) {
        let expectedCost = ruleCheckResult.suggestedCost;

        // If pricePerSack exists and toBranch is enabled, calculate expected cost
        if (ruleCheckResult.pricePerSack && isToBranchEnabled && localTransportDetail.noOfSacks > 0) {
          expectedCost = ruleCheckResult.pricePerSack * localTransportDetail.noOfSacks;
        }

        if (expectedCost && Math.abs(localTransportDetail.estimatedCost - parseFloat(expectedCost)) > 0.01) {
          setRuleOverride(true);
        }
      }

      reset(detailToReset);
    }
  }, [localTransportDetail, reset, ruleCheckResult, isToBranchEnabled]);

  // Handle checkbox change
  const handleCheckboxChange = (event) => {
    const isChecked = event.target.checked;
    setIsToBranchEnabled(isChecked);

    if (!isChecked) {
      setValue("toBranch", null);
      setValue("noOfSacks", 0);
    }
  };

  // Handle manual cost change
  const handleCostChange = (e) => {
    const value = e.target.value;
    setValue("estimatedCost", value);

    // Mark as override if user manually changes the cost
    if (value && ruleCheckResult?.hasRule) {
      const enteredCost = parseFloat(value);
      let expectedCost = ruleCheckResult.suggestedCost;

      // If pricePerSack exists and toBranch is enabled, calculate expected cost
      if (ruleCheckResult.pricePerSack && isToBranchEnabled && watchNoOfSacks > 0) {
        expectedCost = ruleCheckResult.pricePerSack * watchNoOfSacks;
      }

      if (expectedCost && Math.abs(enteredCost - parseFloat(expectedCost)) > 0.01) {
        setRuleOverride(true);
      } else {
        setRuleOverride(false);
      }
    }
  };

  // Filter branches for toBranch (exclude the selected branch)
  const filteredToBranches = useMemo(() => {
    if (!branches || !watchBranch) return branches || [];
    return branches.filter(branch => branch._id !== watchBranch._id);
  }, [branches, watchBranch]);

  // Render rule validation feedback
  const renderRuleValidation = () => {
    if (!isAccountAdmin) {
      return null;
    }

    if (checkingRules || fetchingRules) {
      return (
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={20} />
            <span>Checking transport rules...</span>
          </Box>
        </Grid>
      );
    }

    const hasRule = ruleCheckResult?.hasRule;

    const modeChangedMessage =
      modeChangeInfo && modeChangeInfo.from && modeChangeInfo.to
        ? `Mode changed from ${getModeLabel(modeChangeInfo.from)} to ${getModeLabel(modeChangeInfo.to)}. Rule details remain the same.`
        : null;

    if (hasRule) {
      const hasPricePerSack = ruleCheckResult.pricePerSack && isToBranchEnabled;

      return (
        <Grid item xs={12}>
          <Alert severity="info">
            <strong>Rule matched:</strong> {ruleCheckResult.rule.description || "Standard transport rule"}
            {!isToBranchEnabled && (
              <>
                <br />
                Max distance: {ruleCheckResult.rule.maxDistanceKm} km
              </>
            )}
            {hasPricePerSack && (
              <>
                <br />
                Price per sack: ₹{ruleCheckResult.pricePerSack}
                {watchNoOfSacks > 0 && (
                  <> • Total for {watchNoOfSacks} sack(s): ₹{ruleCheckResult.pricePerSack * watchNoOfSacks}</>
                )}
              </>
            )}
            {!hasPricePerSack && ruleCheckResult.suggestedCost && (
              <> • Suggested cost: ₹{ruleCheckResult.suggestedCost}</>
            )}
            {modeChangedMessage && (
              <>
                <br />
                <br />
                <strong>Note:</strong> {modeChangedMessage}
              </>
            )}
          </Alert>
        </Grid>
      );
    }

    if (watchBranch && watchTransportMode && !modeChangeInfo) {
      return (
        <Grid item xs={12}>
          <Alert severity="info">
            No specific transport rules found for this combination.
          </Alert>
        </Grid>
      );
    }

    return null;
  };

  const handleOnSubmit = (data) => {
    const payload = {
      ...data,
      shipmentId: data.shipmentId?.map((s) => s._id) || [],
      branch: data.branch?._id || null,
      toBranch: isToBranchEnabled && data.toBranch?._id ? data.toBranch._id : null,
      transportationMode: data.transportationMode,
      noOfSacks: isToBranchEnabled ? Number(data.noOfSacks) : undefined,
      distanceKm: Number(data.distanceKm),
      estimatedCost: data.estimatedCost ? Number(data.estimatedCost) : undefined,
      actualCost: data.actualCost ? Number(data.actualCost) : undefined,
      approver: data.approver?._id,
      contractorId: data.contractorId?._id,
      vehicleId: data.vehicleId?._id,
      ...(ruleCheckResult?.hasRule && {
        ruleId: ruleCheckResult.rule._id,
        ruleMaxDistance: ruleCheckResult.rule.maxDistanceKm,
        ruleMaxCost: ruleCheckResult.rule.maxCost,
        isWithinRuleLimit: ruleCheckResult.validation?.isDistanceValid,
        isCostOverridden: ruleOverride
      })
    };

    onSubmit(payload);
  };

  return (
    <AppForm loading={isBusy || loadingModes}>
      <AppForm.Content>
        <Grid container spacing={3}>
          {/* Rule Validation Feedback */}
          {renderRuleValidation()}

          {/* Checkbox and Branch Fields */}
          <Grid item xs={12} sm={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isToBranchEnabled}
                  onChange={handleCheckboxChange}
                />
              }
              label="Transport to Another Branch"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="branch"
              control={control}
              rules={{ required: "Branch is required" }}
              render={({ field, fieldState: { error } }) => (
                <BaseAutocomplete
                  {...field}
                  fullWidth
                  label="From Branch"
                  required
                  value={field.value ?? null}
                  onChange={(_, d) => {
                    field.onChange(d);
                    if (isToBranchEnabled && d?._id !== getValues("toBranch")?._id) {
                      setValue("toBranch", null);
                    }
                  }}
                  getOptionLabel={(o) => o?.name || ""}
                  isOptionEqualToValue={(o, v) => o?._id === v?._id}
                  options={branches || []}
                  loading={loadingBranches}
                  loadingText="Loading Branches..."
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          {/* To Branch Field (Conditional) */}
          {isToBranchEnabled && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="toBranch"
                control={control}
                rules={isToBranchEnabled ? { required: "Destination branch is required" } : {}}
                render={({ field, fieldState: { error } }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="To Branch"
                    required={isToBranchEnabled}
                    value={field.value ?? null}
                    onChange={(_, d) => field.onChange(d)}
                    getOptionLabel={(o) => o?.name || ""}
                    isOptionEqualToValue={(o, v) => o?._id === v?._id}
                    options={filteredToBranches || []}
                    loading={loadingBranches}
                    loadingText="Loading Branches..."
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
          )}

          {/* Transportation Mode */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="transportationMode"
              control={control}
              rules={{ required: "Please select transportation mode." }}
              render={({ field }) => (
                <BaseSelect
                  fullWidth
                  required
                  label="Transportation Mode"
                  options={_mappedTransportModes}
                  getOptionLabel={(opt) => opt.label}
                  getOptionValue={(opt) => opt.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  value={field.value}
                  error={!!errors.transportationMode}
                  helperText={errors.transportationMode?.message}
                  loading={loadingModes}
                  loadingText="Loading Modes..."
                />
              )}
            />
          </Grid>

          {/* Distance with rule validation */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="distanceKm"
              control={control}
              rules={isToBranchEnabled ? {
                required: false,
                min: { value: 0, message: "Distance cannot be negative" }
              } : {
                required: "Distance is required.",
                min: { value: 0.1, message: "Distance must be greater than 0" }
              }}
              render={({ field }) => {
                const hasValidationError = !!errors?.distanceKm;

                return (
                  <TextField
                    {...field}
                    required={!isToBranchEnabled}
                    fullWidth
                    label="Distance (km)"
                    type="number"
                    inputProps={{ step: "0.1", min: "0.1" }}
                    size="small"
                    error={hasValidationError}
                    helperText={errors?.distanceKm?.message}
                  />
                );
              }}
            />
          </Grid>

          {/* Number of Sacks - Conditional (only for branch-to-branch transport) */}
          {isToBranchEnabled && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="noOfSacks"
                control={control}
                rules={{
                  required: "Number of sacks is required for branch-to-branch transport",
                  min: { value: 1, message: "Minimum 1 sack required" }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    required
                    fullWidth
                    label="Number of Sacks"
                    type="number"
                    inputProps={{ step: "1", min: "1" }}
                    size="small"
                    error={!!errors?.noOfSacks}
                    helperText={errors?.noOfSacks?.message}
                  />
                )}
              />
            </Grid>
          )}

          {/* Estimated Cost with auto-fill */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="estimatedCost"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Actual Cost"
                  type="number"
                  inputProps={{ step: "0.01" }}
                  size="small"
                  onChange={handleCostChange}
                  error={!!errors?.estimatedCost}
                  helperText={
                    isAccountAdmin
                      ? errors?.estimatedCost?.message ||
                      (ruleCheckResult?.hasRule && ruleOverride
                        ? `Manual entry (Rule suggests: ₹${ruleCheckResult.pricePerSack && isToBranchEnabled && watchNoOfSacks
                          ? ruleCheckResult.pricePerSack * watchNoOfSacks
                          : ruleCheckResult.suggestedCost
                        })`
                        : ruleCheckResult?.hasRule && ruleCheckResult.pricePerSack && isToBranchEnabled && watchNoOfSacks
                          ? `Calculated: ${watchNoOfSacks} sacks × ₹${ruleCheckResult.pricePerSack} = ₹${ruleCheckResult.pricePerSack * watchNoOfSacks}`
                          : ruleCheckResult?.hasRule && ruleCheckResult.suggestedCost
                            ? `Auto-filled from rules`
                            : "")
                      : errors?.estimatedCost?.message || ""
                  }
                  InputProps={{
                    endAdornment: checkingRules ? (
                      <CircularProgress size={20} />
                    ) : null
                  }}
                />
              )}
            />
          </Grid>

          {/* Shipment Field */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="shipmentId"
              control={control}
              render={({ field }) => (
                <BaseAutocomplete
                  {...field}
                  multiple
                  fullWidth
                  label="Shipment"
                  value={field.value || []}
                  onChange={(_, data) => field.onChange(data)}
                  onInputChange={(_, newInput) =>
                    setShipmentSearchInput(newInput)
                  }
                  getOptionLabel={(option) =>
                    option?.shipmentId
                      ? `${option.shipmentId}`
                      : ""
                  }
                  isOptionEqualToValue={(o, v) => o?._id === v?._id}
                  options={shipments?.data || []}
                  loading={loadingShipments}
                  loadingText="Loading Shipments..."
                  error={!!errors.shipmentId}
                  helperText={errors.shipmentId?.message}
                />
              )}
            />
          </Grid>

          {/* Contractor Field */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="contractorId"
              control={control}
              render={({ field }) => (
                <BaseAutocomplete
                  {...field}
                  fullWidth
                  label="Contractor"
                  value={field.value ?? null}
                  onChange={(_, data) => {
                    field.onChange(data);
                  }}
                  onInputChange={(_, newInput) =>
                    setContractorSearchInput(newInput)
                  }
                  getOptionLabel={(option) =>
                    option?.contractorId
                      ? `${option.contractorId} - ${option.name || ""}`
                      : ""
                  }
                  isOptionEqualToValue={(o, v) => o?._id === v?._id}
                  options={contractors?.data || []}
                  loading={loadingContractors}
                  loadingText="Loading Contractors..."
                  error={!!errors.contractorId}
                  helperText={errors.contractorId?.message}
                />
              )}
            />
          </Grid>

          {/* Vehicle Field */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="vehicleId"
              control={control}
              render={({ field }) => (
                <BaseAutocomplete
                  {...field}
                  fullWidth
                  label="Vehicle"
                  value={field.value ?? null}
                  onChange={(_, data) => field.onChange(data)}
                  getOptionLabel={(option) =>
                    option?.vehicle_id && option?.registration_number
                      ? `${option.vehicle_id} - ${option.registration_number}`
                      : ""
                  }
                  isOptionEqualToValue={(o, v) => o?._id === v?._id}
                  options={vehicleOptions}
                  disabled={!vehicleOptions.length}
                  error={!!errors.vehicleId}
                  helperText={
                    errors.vehicleId?.message ||
                    (!vehicleOptions.length
                      ? "Please select a contractor first"
                      : "")
                  }
                />
              )}
            />
          </Grid>

          {/* Actual Cost - only show when editing */}
          {localTransportDetail && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="actualCost"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Actual Cost"
                    type="number"
                    inputProps={{ step: "0.01" }}
                    size="small"
                    error={!!errors?.actualCost}
                    helperText={errors?.actualCost?.message}
                  />
                )}
              />
            </Grid>
          )}

          {/* Approver */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="approver"
              control={control}
              rules={{ required: "Approver is required" }}
              render={({ field }) => (
                <BaseAutocomplete
                  {...field}
                  fullWidth
                  required
                  label="Approver"
                  value={field.value ?? null}
                  onChange={(_, data) => field.onChange(data)}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(o, v) => o?._id === v?._id}
                  options={approvers}
                  loading={loadingApprovers}
                  loadingText="Loading Approvers..."
                  error={!!errors.approver}
                  helperText={errors.approver?.message}
                />
              )}
            />
          </Grid>

          {/* Remarks */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="remarks"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Remarks"
                  multiline
                  rows={2}
                  size="small"
                />
              )}
            />
          </Grid>
        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleOnSubmit)}
          disabled={checkingRules}
        >
          {localTransportDetail ? "Update" : "Request"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default LocalTransportForm;